import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Paper,
  Stack,
  Button,
  List,
  ThemeIcon,
  Box,
  Badge,
  useComputedColorScheme,
} from "@mantine/core";
import { IconCheck, IconRocket, IconStar } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { motion } from "framer-motion";
import { AnimateIn, staggerContainer, fadeUpItem } from "./AnimateIn";
import { ContactFormModal } from "./ContactFormModal";

const plans = [
  {
    name: "Standard",
    // Keep in step with STRIPE_PRODUCTS.STANDARD.amount in the backend
    // (49900 cents). This is marketing copy, not a billing source of truth,
    // but a mismatch means the site advertises one price and Checkout charges
    // another.
    price: 499,
    description: "Perfect for growing facilities",
    features: [
      "Unlimited users",
      "Complete resident management",
      "Incident tracking & reporting",
      "Staff presence & access control",
      "Multi-facility support",
      "Real-time dashboard",
      "Custom forms builder",
      "Medication management",
      "Medication administration tracking",
      "Prescription tracking",
      "CSV exports",
      "HIPAA-compliant audit logs",
      "Email support",
    ],
    highlighted: false,
    cta: "Request Demo",
  },
  {
    name: "Premium",
    // Matches STRIPE_PRODUCTS.PREMIUM.amount (74900 cents).
    price: 749,
    description: "For facilities needing advanced compliance features",
    features: [
      "Everything in Standard",
      "Advanced task automation & worklist subscriptions",
      "Facility task dashboard (open / overdue / missed)",
      "Facility compliance dashboard",
      "Resident compliance scoring",
      "Appointment gate-metrics reporting",
      "Bulk PDF export for state surveys",
      "Compliance rule library",
      "EMR integration webhooks",
      "Compliance CSV exports",
    ],
    highlighted: true,
    cta: "Contact Sales",
  },
];

export function PricingSection() {
  const [
    contactModalOpened,
    { open: openContactModal, close: closeContactModal },
  ] = useDisclosure(false);
  const computedColorScheme = useComputedColorScheme("light");

  return (
    <Box
      component="section"
      id="pricing"
      py={100}
      bg={computedColorScheme === "dark" ? "#25262B" : "#f8f9fa"}
    >
      <Container size="xl">
        <AnimateIn>
          <Stack align="center" gap="md" mb={60}>
            <Title order={2} size="2.5rem" fw={700} ta="center">
              Simple,{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Transparent Pricing
              </Text>
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={700}>
              Choose the plan that fits your needs. All plans include a 7-day
              free trial. No credit card required.
            </Text>
          </Stack>
        </AnimateIn>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <SimpleGrid
            cols={{ base: 1, md: 2 }}
            spacing="xl"
            maw={1000}
            mx="auto"
          >
            {plans.map((plan) => (
              <motion.div key={plan.name} variants={fadeUpItem}>
                <Paper
                  p="xl"
                  radius="lg"
                  shadow={plan.highlighted ? "xl" : "sm"}
                  style={{
                    height: "100%",
                    border: plan.highlighted
                      ? "2px solid #4a8c71"
                      : "1px solid #e9ecef",
                    position: "relative",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow =
                      "0 20px 40px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  {plan.highlighted && (
                    <Box
                      style={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    >
                      <Badge
                        size="lg"
                        leftSection={<IconStar size={14} />}
                        style={{
                          background:
                            "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                        }}
                      >
                        Most Popular
                      </Badge>
                    </Box>
                  )}

                  <Stack gap="lg">
                    <Box>
                      <Text size="xl" fw={700} mb={4}>
                        {plan.name}
                      </Text>
                      <Text size="sm" c="dimmed" mb="xl">
                        {plan.description}
                      </Text>
                      <Box>
                        <Text
                          size="3rem"
                          fw={700}
                          component="span"
                          style={{
                            background:
                              "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          ${plan.price}
                        </Text>
                        <Text size="md" c="dimmed" component="span">
                          /facility/month
                        </Text>
                      </Box>
                    </Box>

                    <Button
                      size="lg"
                      fullWidth
                      variant={plan.highlighted ? "filled" : "light"}
                      leftSection={<IconRocket size={18} />}
                      styles={
                        plan.highlighted
                          ? {
                              root: {
                                background:
                                  "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                              },
                            }
                          : undefined
                      }
                      onClick={openContactModal}
                    >
                      {plan.cta}
                    </Button>

                    <List
                      spacing="sm"
                      size="sm"
                      icon={
                        <ThemeIcon
                          size={20}
                          radius="xl"
                          color="green"
                          variant="light"
                        >
                          <IconCheck size={14} stroke={3} />
                        </ThemeIcon>
                      }
                    >
                      {plan.features.map((feature) => (
                        <List.Item key={feature}>{feature}</List.Item>
                      ))}
                    </List>
                  </Stack>
                </Paper>
              </motion.div>
            ))}
          </SimpleGrid>
        </motion.div>

        <AnimateIn delay={0.15}>
          <Stack align="center" mt="xl" gap={4}>
            <Text size="sm" c="dimmed" ta="center">
              Personalized onboarding · White-glove setup · Dedicated support
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Pricing scales with your organization. Add or remove facilities at
              any time.
            </Text>
          </Stack>
        </AnimateIn>
      </Container>

      <ContactFormModal
        opened={contactModalOpened}
        onClose={closeContactModal}
      />
    </Box>
  );
}
